import { apiTryFetchRetry } from '@editor/utils/apiFetchRetry';
import type { SubscriptionPrices } from '@repo/subscription/api';
import {
  Stripe,
  StripeAddressElementChangeEvent,
  StripeAddressElementOptions,
  StripeElements,
  StripeError,
} from '@stripe/stripe-js';

import { GenericApiResponse } from '../types';

import { USER_API_URL } from './constants';

export type Intent = {
  id?: string;
  clientSecret: string;
};

/**
 * Confirm intent data.
 *
 * @param stripe useStripe object
 * @param elements object obtained using useElements
 * @param clientSecret client secret extracted from the intent
 * @param returnUrl URL to return to after payment confirmation (only relevant for payment methods like PayPal)
 */
interface ConfirmIntentData {
  stripe: Stripe;
  elements: StripeElements;
  returnUrl: string;
  setupIntentClientSecret?: string | null;
  paymentIntentClientSecret?: string | null;
}

interface SubscriptionIntentData {
  prices: SubscriptionPrices;
  promoCode?: string | null;
}

export type StripeBillingInfo = NonNullable<
  StripeAddressElementChangeEvent['value']
>;

export type StripeBillingInfoDefaultValues = NonNullable<
  StripeAddressElementOptions['defaultValues']
>;

export interface BillingInfo {
  name: string;
  firstName?: string | undefined;
  lastName?: string | undefined;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode?: string | null;
    country: string;
  };
  phone?: string | undefined;
}

export type SetupIntentData = SubscriptionIntentData; // Will be more complex in the future

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createApi = (url: string) => {
  const setupIntent = async (
    data: SetupIntentData
  ): Promise<
    GenericApiResponse<{
      intentId: string;
      clientSecret: string;
      paymentMethods: string[];
    }>
  > => {
    return await apiTryFetchRetry(`${url}/billing/intent/create`, {
      method: 'POST',
      body: JSON.stringify({ subscription: data }),
    });
  };

  /**
   * Confirm a setup/payment intent.
   */
  const confirmIntent = async ({
    stripe,
    elements,
    returnUrl,
    paymentIntentClientSecret,
    setupIntentClientSecret,
  }: ConfirmIntentData): Promise<
    { success: true } | { success: false; error: StripeError }
  > => {
    if (!paymentIntentClientSecret && !setupIntentClientSecret) {
      throw new Error('No client secret found');
    }

    const isPaymentIntent = Boolean(paymentIntentClientSecret);

    const confirm = isPaymentIntent
      ? stripe.confirmPayment
      : stripe.confirmSetup;

    const response = await confirm({
      elements,
      redirect: 'if_required',
      clientSecret: paymentIntentClientSecret ?? setupIntentClientSecret ?? '',
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (!response.error) {
      return {
        success: true,
      };
    }

    return { success: false, error: response.error };
  };

  const updateBillingInfo = async (
    data: BillingInfo
  ): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/billing/info/update`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  };

  const getBillingInfo = async (): Promise<
    GenericApiResponse<{
      billingInfo: BillingInfo;
    }>
  > => {
    return await apiTryFetchRetry(`${url}/billing/info`, {
      method: 'GET',
    });
  };

  const getPaymentFailureReason = async (
    intentId: string
  ): Promise<GenericApiResponse<{ intent: { errorCode: string } }>> => {
    return await apiTryFetchRetry(
      `${url}/subscription/payment/${intentId}/failure_reason`,
      {
        method: 'GET',
      }
    );
  };

  return {
    // payment
    setupIntent,
    confirmIntent,
    updateBillingInfo,
    getBillingInfo,
    getPaymentFailureReason,
  };
};

export default createApi(USER_API_URL);
